import { AdminHomeApi } from './adminHomePageApi';

const ADMIN_HOME_STATUSES = [
    "aguardando inspeção e laudo",
    "aguardando faturamento",
    "aguardando estimativa",
    "estimativa entregue",
    "aguardando equipamento",
    "pronto para retirada",
    "enviado",
    "equipamento em analise",
    "autorizar manutencao",
    "pendente de aprovação",
    "aguardando orçamento",
    "aprovado para manutencao",
    "finalizado",
];

const NOTE_FILE_ALIASES = [
  'nota_remessa_file_name',
  'notaRemessaFileName',
  'nota_remessa',
  'notaRemessa',
];

const BUDGET_FILE_ALIASES = [
  'budget_file_name',
  'budgetFileName',
  'orcamento_file_name',
  'orcamentoFileName',
  'nome_arquivo_orcamento',
  'nomeArquivoOrcamento',
];

function normalizeStatus(value) {
  return String(value || '').trim().toLowerCase();
}

function createDefaultCounters() {
  return ADMIN_HOME_STATUSES.reduce((acc, status) => {
    acc[status] = 0;
    return acc;
  }, {});
}

function buildCountersFromApi(rawCounters = {}) {
  const counters = createDefaultCounters();

  ADMIN_HOME_STATUSES.forEach((status) => {
    const parsed = Number(rawCounters[status]);
    counters[status] = Number.isFinite(parsed) ? parsed : 0;
  });

  return counters;
}

function hasOwn(obj, key) {
  return Object.prototype.hasOwnProperty.call(obj || {}, key);
}

function extractValueByAliases(source = {}, aliases = []) {
  const alias = aliases.find((candidate) => hasOwn(source, candidate));
  return alias ? source[alias] : null;
}

function hasNotaRemessaField(quotation = {}) {
  if (NOTE_FILE_ALIASES.some((alias) => hasOwn(quotation, alias))) {
    return true;
  }

  if (hasOwn(quotation, 'nota_remessa_file')) {
    return true;
  }

  if (Array.isArray(quotation.serviceFiles)) {
    return quotation.serviceFiles.some((file) => {
      const type = normalizeStatus(file?.type || file?.fileType);
      return type === 'nota_remessa' || type === 'nota remessa';
    });
  }

  return false;
}

function extractNotaRemessaFileName(quotation = {}) {
  const fromAliases = extractValueByAliases(quotation, NOTE_FILE_ALIASES);
  if (fromAliases) {
    return fromAliases;
  }

  const nestedNota = quotation.nota_remessa_file;
  if (nestedNota && typeof nestedNota === 'object') {
    return nestedNota.file_name || nestedNota.fileName || nestedNota.nome || null;
  }

  if (Array.isArray(quotation.serviceFiles)) {
    const notaRemessa = quotation.serviceFiles.find((file) => {
      const type = normalizeStatus(file?.type || file?.fileType);
      return type === 'nota_remessa' || type === 'nota remessa';
    });

    if (notaRemessa) {
      return notaRemessa.fileName || notaRemessa.file_name || notaRemessa.nome || null;
    }
  }

  return null;
}

function extractBudgetFileName(quotation = {}) {
  const fromAliases = extractValueByAliases(quotation, BUDGET_FILE_ALIASES);
  if (fromAliases) {
    return fromAliases;
  }

  const nestedBudget = quotation.budget_file || quotation.orcamento_file || quotation.budgetFile || quotation.orcamentoFile;
  if (nestedBudget && typeof nestedBudget === 'object') {
    return extractValueByAliases(nestedBudget, BUDGET_FILE_ALIASES)
      || nestedBudget.file_name
      || nestedBudget.fileName
      || nestedBudget.nome
      || null;
  }

  if (Array.isArray(quotation.serviceFiles)) {
    const budgetFile = quotation.serviceFiles.find((file) => {
      const type = normalizeStatus(file?.type || file?.fileType);
      return type === 'orcamento' || type === 'orçamento' || type === 'budget';
    });

    if (budgetFile) {
      return extractValueByAliases(budgetFile, BUDGET_FILE_ALIASES)
        || budgetFile.fileName
        || budgetFile.file_name
        || budgetFile.nome
        || null;
    }
  }

  return null;
}

function collectServiceFileNames(quotation = {}) {
  const candidates = [];
  const directNames = [
    quotation.service_file_names,
    quotation.serviceFileNames,
  ];
  const serviceFilesGroups = [
    quotation.serviceFiles,
    quotation.service_files,
  ];

  directNames.forEach((group) => {
    if (!Array.isArray(group)) return;
    group.forEach((name) => {
      if (typeof name === 'string' && name.trim()) {
        candidates.push(name.trim());
      }
    });
  });

  serviceFilesGroups.forEach((group) => {
    if (!Array.isArray(group)) return;

    group.forEach((file) => {
      if (typeof file === 'string' && file.trim()) {
        candidates.push(file.trim());
        return;
      }

      if (file && typeof file === 'object') {
        const type = String(file.type || file.fileType || '').toLowerCase();
        if (type !== 'nota_remessa' && type !== 'nota remessa') return;
        const name = file.fileName || file.file_name || file.nome || file.name;
        if (typeof name === 'string' && name.trim()) {
          candidates.push(name.trim());
        }
      }
    });
  });

  const fallbackNames = [
    extractBudgetFileName(quotation),
    extractNotaRemessaFileName(quotation),
  ].filter(Boolean);

  fallbackNames.forEach((name) => {
    if (typeof name === 'string' && name.trim()) {
      candidates.push(name.trim());
    }
  });

  return Array.from(new Set(candidates));
}

function formatHomeData(data) {
  const quotations = (data.cotacoes || data.HOME_Cotacoes || []).map((quotation) => formatQuotationData(quotation));
  const hasApiCounters = Boolean(data.contadores || data.counters);

  if (!hasApiCounters && quotations.length > 0) {
    const grouped = groupByStatus(quotations);
    return {
      HOME_Cotacoes: grouped.cotacoesFiltradas,
      contadores: grouped.contadores,
      lastUpdate: data.last_update || new Date().toISOString(),
    };
  }

  return {
    HOME_Cotacoes: quotations,
    contadores: buildCountersFromApi(data.contadores || data.counters || {}),
    lastUpdate: data.last_update || new Date().toISOString(),
  };
}

function formatQuotationData(quotation) {
  return {
    id: quotation.id || quotation.codigo,
    tipo: quotation.type || quotation.tipo,
    status: normalizeStatus(quotation.status),
    data: quotation.created_at || quotation.data || quotation.date,
    cnpj: quotation.cnpj || quotation.client_cnpj,
    cliente: quotation.cliente || quotation.client_name || quotation.client,
    user_id: quotation.user_id || quotation.client_id,
    valor_resposta: quotation.valor_resposta,
    assumed_by_user_id: quotation.assumed_by_user_id,
    assumed_by_user_name: quotation.assumed_by_user_name,
    assumed_by_workshop_user_id: quotation.assumed_by_workshop_user_id,
    assumed_by_workshop_user_name: quotation.assumed_by_workshop_user_name,
    assumed_by_workshop_at: quotation.assumed_by_workshop_at,
    budgetFileName: extractBudgetFileName(quotation),
    notaRemessaFileName: extractNotaRemessaFileName(quotation),
    serviceFileNames: collectServiceFileNames(quotation),
    hasNotaRemessaField: hasNotaRemessaField(quotation),
  };
}

function formatQuotationsList(data) {
  if (Array.isArray(data)) {
    return data.map((quotation) => formatQuotationData(quotation));
  }

  if (data.cotacoes) {
    return data.cotacoes.map((quotation) => formatQuotationData(quotation));
  }

  return [];
}

function formatEquipmentData(equipment) {
  return {
    id: equipment.id,
    nome: equipment.name || equipment.nome,
    marca: equipment.brand || equipment.marca,
    modelo: equipment.model || equipment.modelo,
    numero_serie: equipment.serial_number || equipment.numero_serie,
    cliente: equipment.client || equipment.cliente,
    status: equipment.status,
  };
}

function formatarData(dataString) {
  if (!dataString) return '--';

  if (typeof dataString === 'string' && dataString.includes('/')) {
    return dataString;
  }

  const data = new Date(dataString);
  return data.toLocaleDateString('pt-BR');
}

function formatarMoeda(valor) {
  if (valor == null || valor === '' || valor === 0) return '-';

  const numValue = typeof valor === 'string' ? parseFloat(valor) : valor;

  if (Number.isNaN(numValue)) {
    return 'Valor inválido';
  }

  return numValue.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}

function groupByStatus(quotations) {
  const contadores = createDefaultCounters();

  const cotacoesFiltradas = quotations.filter((quotation) =>
    ADMIN_HOME_STATUSES.includes(normalizeStatus(quotation.status))
  );

  cotacoesFiltradas.forEach((quotation) => {
    const status = normalizeStatus(quotation.status);
    if (Object.prototype.hasOwnProperty.call(contadores, status)) {
      contadores[status] += 1;
    }
  });

  return {
    contadores,
    cotacoesFiltradas,
  };
}

function getHomeData() {
  return AdminHomeApi.getHomeData().then((response) => formatHomeData(response.data));
}

function getHomeDataByStatus(status) {
  return AdminHomeApi.getHomeDataByStatus(status)
    .then((response) => {
      return {
        dadosFiltrados: formatQuotationsList(response.data),
        totalValor: response.data.total_valor || response.data.totalValue || 0,
      };
    });
}

function searchEquipment(equipmentId) {
  return AdminHomeApi.searchEquipment(equipmentId)
    .then((response) => formatEquipmentData(response.data))
    .catch((error) => {
      throw new Error(error?.message || 'Busca de equipamento indisponível no backend atual.');
    });
}

function refreshHomeData() {
  return AdminHomeApi.refreshHomeData().then((response) => formatHomeData(response.data));
}

function assumeQuotation(quotationId, user) {
  const payload = {
    quotationId: quotationId,
    userId: user.id || user.user_id,
    userName: user.name || user.nome || user.userName,
  };
  return AdminHomeApi.assumeQuotation(payload).then((response) => response.data);
}

function unassignCommercialQuotation(quotationId) {
  const payload = { quotationId: quotationId };
  return AdminHomeApi.unassignCommercialQuotation(payload).then((response) => response.data);
}

function unassignWorkshopQuotation(quotationId) {
  const payload = { quotationId: quotationId };
  return AdminHomeApi.unassignWorkshopQuotation(payload).then((response) => response.data);
}

function updateQuotationStatus(id, status) {
  return AdminHomeApi.updateQuotationStatus(id, status).then((response) => response.data);
}

export const AdminHomeService = {
  getHomeData,
  getHomeDataByStatus,
  searchEquipment,
  refreshHomeData,
  assumeQuotation,
  unassignCommercialQuotation,
  unassignWorkshopQuotation,
  updateQuotationStatus,
  formatHomeData,
  formatQuotationData,
  formatQuotationsList,
  formatEquipmentData,
  formatarData,
  formatarMoeda,
  groupByStatus,
};

